import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Activity, RefreshCw, AlertTriangle, CheckCircle2, Clock, Search,
  Zap, Gauge, TrendingUp, ListChecks, ScrollText, Webhook,
  ShieldAlert, Timer, Ban, CircleAlert, LineChart, Database,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const t = typeof window !== 'undefined'
    ? window.localStorage.getItem('elite_admin_token') : null;
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const fmtMs = (n) => (n == null ? '—' : `${Math.round(n)} ms`);
const fmtDate = (s) => (!s ? '—' : new Date(s).toLocaleString());
const REFRESH_OPTIONS = [0, 10, 30, 60];

// ─── Presentational primitives ─────────────────────────────────────────────
const Card = ({ title, icon: Icon, children, action, testid, tone = 'default' }) => (
  <div
    className={`rounded-xl border p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900 ${
      tone === 'red' ? 'border-red-300 bg-red-50 dark:bg-red-950/30'
        : tone === 'orange' ? 'border-orange-300 bg-orange-50 dark:bg-orange-950/30'
        : tone === 'yellow' ? 'border-yellow-300 bg-yellow-50 dark:bg-yellow-950/30'
        : tone === 'green' ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30'
        : 'border-gray-200 bg-white'
    }`}
    data-testid={testid}
  >
    <div className="mb-3 flex items-center justify-between">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
        {Icon && <Icon className="h-4 w-4 text-red-600" />}
        {title}
      </div>
      {action}
    </div>
    {children}
  </div>
);

const KpiTile = ({ label, value, subtitle, tone, testid }) => (
  <div
    data-testid={testid}
    className={`rounded-lg border p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 ${
      tone === 'red' ? 'border-red-300 bg-red-50 dark:bg-red-950/30'
        : tone === 'orange' ? 'border-orange-300 bg-orange-50 dark:bg-orange-950/30'
        : tone === 'yellow' ? 'border-yellow-300 bg-yellow-50 dark:bg-yellow-950/30'
        : tone === 'green' ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30'
        : 'border-gray-200 bg-white'
    }`}
  >
    <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
    <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{value ?? '—'}</div>
    {subtitle && <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{subtitle}</div>}
  </div>
);

const StatusDot = ({ status }) => {
  const cls = {
    green:  'bg-emerald-500',
    yellow: 'bg-yellow-500',
    orange: 'bg-orange-500',
    red:    'bg-red-500',
  }[status] || 'bg-gray-400';
  return <span className={`inline-block h-2.5 w-2.5 rounded-full ${cls}`} />;
};

// ─── Ops Center page ──────────────────────────────────────────────────────
export default function AdminETGOperations() {
  const [summary, setSummary]        = useState(null);
  const [canary, setCanary]          = useState(null);
  const [perf, setPerf]              = useState(null);
  const [errList, setErrList]        = useState([]);
  const [retryQ, setRetryQ]          = useState([]);
  const [webhookRows, setWebhookRows]= useState([]);
  const [reconIssues, setReconIssues]= useState([]);
  const [loading, setLoading]        = useState(true);
  const [refreshEvery, setRefreshEvery] = useState(30);
  const [lastFetch, setLastFetch]    = useState(null);
  const [search, setSearch]          = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [timelineBid, setTimelineBid]= useState(null);
  const [timeline, setTimeline]      = useState(null);
  const [apiLog, setApiLog]          = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, p, e, rq, wh, ri] = await Promise.all([
        axios.get(`${API}/api/admin/ratehawk/phase8/summary`,        { headers: authHeaders() }),
        axios.get(`${API}/api/admin/ratehawk/phase8/canary`,         { headers: authHeaders() }),
        axios.get(`${API}/api/admin/ratehawk/phase8/performance`,    { headers: authHeaders() }),
        axios.get(`${API}/api/admin/ratehawk/phase7/errors?limit=50`,{ headers: authHeaders() }),
        axios.get(`${API}/api/admin/ratehawk/phase7/retry-queue`,    { headers: authHeaders() }),
        axios.get(`${API}/api/admin/ratehawk/webhooks?limit=20`,     { headers: authHeaders() }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/api/admin/ratehawk/reconcile/issues?limit=30`, { headers: authHeaders() }).catch(() => ({ data: { items: [] } })),
      ]);
      setSummary(s.data); setCanary(c.data); setPerf(p.data);
      setErrList(e.data.items || []);
      setRetryQ(rq.data.items || []);
      setWebhookRows(wh.data.items || []);
      setReconIssues(ri.data.items || []);
      setLastFetch(new Date());
    } catch (err) {
      console.error('Ops Center load failed:', err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!refreshEvery) return undefined;
    const id = setInterval(load, refreshEvery * 1000);
    return () => clearInterval(id);
  }, [refreshEvery, load]);

  // ── Correlation / booking / partner_order_id search ──────────────────
  const runSearch = async () => {
    if (!search.trim()) return;
    setSearchResult({ loading: true });
    try {
      // Try correlation first, then booking id, then partner_order_id
      const q = search.trim();
      try {
        const r = await axios.get(
          `${API}/api/admin/ratehawk/search/correlation/${encodeURIComponent(q)}`,
          { headers: authHeaders() },
        );
        if (r.data && r.data.booking) { setSearchResult(r.data); return; }
      } catch { /* fall through */ }
      try {
        const r = await axios.get(
          `${API}/api/admin/ratehawk/timeline/${encodeURIComponent(q)}`,
          { headers: authHeaders() },
        );
        setSearchResult({ booking: r.data.booking, cancellations: r.data.cancellations, refunds: r.data.refunds });
      } catch {
        setSearchResult({ error: 'No records found for that identifier.' });
      }
    } catch {
      setSearchResult({ error: 'Search failed.' });
    }
  };

  const openTimeline = async (bid) => {
    setTimelineBid(bid);
    try {
      const r = await axios.get(
        `${API}/api/admin/ratehawk/timeline/${encodeURIComponent(bid)}`,
        { headers: authHeaders() },
      );
      setTimeline(r.data);
    } catch { setTimeline({ error: 'Failed to load timeline.' }); }
  };

  const exportJson = (data, name) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${name}-${Date.now()}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportCsv = (rows, name) => {
    if (!rows || rows.length === 0) return;
    const cols = Array.from(rows.reduce((s, r) => { Object.keys(r).forEach((k) => s.add(k)); return s; }, new Set()));
    const csv = [cols.join(',')].concat(
      rows.map((r) => cols.map((c) => JSON.stringify(r[c] ?? '')).join(',')),
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${name}-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // Compute overall traffic-light for header banner
  const overall = canary?.overall || 'green';
  const kpis = summary?.kpis || {};
  const errAgg = summary?.errors || {};

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 p-6" data-testid="etg-ops-center">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">ETG Operations Center</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Live monitoring · correlation search · retry queue · error analytics · reconciliation
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className={`flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${
            overall === 'red' ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-100'
              : overall === 'orange' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-100'
              : overall === 'yellow' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-100'
              : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-100'
          }`} data-testid="ops-overall-status">
            <StatusDot status={overall} /> {overall.toUpperCase()}
          </div>
          <select
            data-testid="ops-refresh-select"
            className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-900"
            value={refreshEvery}
            onChange={(e) => setRefreshEvery(Number(e.target.value))}
          >
            {REFRESH_OPTIONS.map((n) => (
              <option key={n} value={n}>{n === 0 ? 'Manual' : `Auto ${n}s`}</option>
            ))}
          </select>
          <Button size="sm" variant="outline" onClick={load} data-testid="ops-refresh-btn">
            <RefreshCw className={`mr-2 h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Correlation search */}
      <Card title="Correlation-ID / Booking Search" icon={Search} testid="ops-search-card"
             action={<span className="text-[11px] text-gray-500">
               correlation_id · booking_id · partner_order_id · etg_order_id
             </span>}>
        <div className="flex gap-2">
          <Input
            data-testid="ops-search-input"
            placeholder="Enter an identifier to trace across all services…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && runSearch()}
          />
          <Button onClick={runSearch} data-testid="ops-search-submit">Search</Button>
        </div>
        {searchResult && !searchResult.loading && (
          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm dark:border-gray-800 dark:bg-gray-900/50">
            {searchResult.error && <div className="text-red-600">{searchResult.error}</div>}
            {searchResult.booking && (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-3">
                  <Badge>{searchResult.booking.state}</Badge>
                  <span className="text-xs text-gray-500">
                    booking_id · <button
                      className="font-mono text-blue-600 hover:underline"
                      onClick={() => openTimeline(searchResult.booking.id)}
                      data-testid={`open-timeline-${searchResult.booking.id}`}
                    >{searchResult.booking.id}</button>
                  </span>
                  <span className="text-xs text-gray-500">
                    partner_order_id · <span className="font-mono">{searchResult.booking.partner_order_id}</span>
                  </span>
                  <span className="text-xs text-gray-500">
                    correlation · <span className="font-mono">{searchResult.booking.correlation_id}</span>
                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  {searchResult.api_logs?.length ?? 0} API log rows · {searchResult.events?.length ?? searchResult.booking.events?.length ?? 0} timeline events
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Booking Health Widget */}
      <Card title="Booking Health" icon={ShieldAlert} testid="ops-booking-health"
             action={<span className="text-[11px] text-gray-500">
               window {canary?.window_minutes ?? 15} min · last {lastFetch ? lastFetch.toLocaleTimeString() : '—'}
             </span>}>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {canary?.signals && Object.entries(canary.signals).map(([k, v]) => (
            <KpiTile key={k}
                       label={k.replace(/_/g, ' ')}
                       value={v.count}
                       tone={v.status}
                       testid={`ops-signal-${k}`} />
          ))}
        </div>
      </Card>

      {/* KPI cards */}
      <Card title="Key Performance Indicators (24h)" icon={Activity} testid="ops-kpis">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
          <KpiTile label="Total Bookings"    value={kpis.total_bookings}    testid="kpi-total-bookings" />
          <KpiTile label="Pending"           value={kpis.pending}           testid="kpi-pending" tone={kpis.pending > 5 ? 'yellow' : undefined} />
          <KpiTile label="Confirmed"         value={kpis.confirmed}         testid="kpi-confirmed" tone={kpis.confirmed > 0 ? 'green' : undefined} />
          <KpiTile label="Cancelled"         value={kpis.cancelled}         testid="kpi-cancelled" />
          <KpiTile label="Expired"           value={kpis.expired}           testid="kpi-expired" tone={kpis.expired > 3 ? 'orange' : undefined} />
          <KpiTile label="Failed"            value={kpis.failed}            testid="kpi-failed"   tone={kpis.failed > 0 ? 'red' : undefined} />
          <KpiTile label="Webhook Events"    value={kpis.webhook_events_24h} testid="kpi-webhooks" />
          <KpiTile label="Cancellations"     value={kpis.cancellations}     testid="kpi-cancellations" />
          <KpiTile label="Refunds Pending"   value={kpis.refunds_pending}   testid="kpi-refunds-pending" />
          <KpiTile label="Refunds Settled"   value={kpis.refunds_settled}   testid="kpi-refunds-settled" />
          <KpiTile label="Sync Runs"         value={kpis.sync_history}      testid="kpi-syncs" />
          <KpiTile label="Reconciliation Runs" value={kpis.reconciliation_runs} testid="kpi-recon-runs" />
          <KpiTile label="Timeouts (24h)"    value={errAgg.timeouts ?? 0}   testid="kpi-timeouts" tone={errAgg.timeouts > 5 ? 'orange' : undefined} />
          <KpiTile label="Provider Errors"   value={errAgg.provider_failures ?? 0} testid="kpi-provider" tone={errAgg.provider_failures > 5 ? 'orange' : undefined} />
          <KpiTile label="Sold Out"          value={errAgg.soldout ?? 0}    testid="kpi-soldout" />
          <KpiTile label="Validation Errors" value={errAgg.validation_failures ?? 0} testid="kpi-validation" />
          <KpiTile label="Unknowns"          value={errAgg.unknowns ?? 0}   testid="kpi-unknowns" />
          <KpiTile label="Recovery Success"  value={errAgg.recovery_success ?? 0} testid="kpi-recovery" />
        </div>
      </Card>

      {/* Certification Package downloads */}
      <Card title="ETG Certification Package" icon={Database} testid="ops-cert-downloads"
             action={<span className="text-[11px] text-gray-500">
               100% code + live-verified · ready to send
             </span>}>
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'report-pdf',     label: 'Report (PDF)',       filename: 'certification_report.pdf' },
            { key: 'report-html',    label: 'Report (HTML)',      filename: 'certification_report.html' },
            { key: 'report-full-md', label: 'Report + Checklist (MD)', filename: 'certification_report_full.md' },
            { key: 'checklist-md',   label: 'Checklist (MD)',     filename: 'checklist.md' },
            { key: 'checklist-json', label: 'Checklist (JSON)',   filename: 'checklist.json' },
            { key: 'summary-json',   label: 'Summary (JSON)',     filename: 'summary.json' },
            { key: 'submission-tar', label: 'Submission (tar.gz)', filename: 'etg_certification_submission.tar.gz' },
          ].map(({ key, label, filename }) => (
            <Button key={key} variant="outline" size="sm"
                     data-testid={`cert-dl-${key}`}
                     onClick={async () => {
                       try {
                         const r = await axios.get(
                           `${API}/api/admin/certification/${key}`,
                           { headers: authHeaders(), responseType: 'blob' },
                         );
                         const url = window.URL.createObjectURL(new Blob([r.data]));
                         const a = document.createElement('a');
                         a.href = url; a.download = filename;
                         document.body.appendChild(a); a.click();
                         a.remove(); window.URL.revokeObjectURL(url);
                         toast.success(`Downloaded ${filename}`);
                       } catch (err) {
                         toast.error(`Download failed: ${err?.response?.status || err.message}`);
                       }
                     }}>
              {label}
            </Button>
          ))}
        </div>
      </Card>

      <Tabs defaultValue="perf" className="space-y-3">
        <TabsList data-testid="ops-tabs">
          <TabsTrigger value="perf"    data-testid="tab-performance"><Gauge className="mr-1.5 h-3.5 w-3.5" />Performance</TabsTrigger>
          <TabsTrigger value="errors"  data-testid="tab-errors"><CircleAlert className="mr-1.5 h-3.5 w-3.5" />Error Analytics</TabsTrigger>
          <TabsTrigger value="retry"   data-testid="tab-retry"><RefreshCw className="mr-1.5 h-3.5 w-3.5" />Retry Queue</TabsTrigger>
          <TabsTrigger value="recon"   data-testid="tab-reconciliation"><ListChecks className="mr-1.5 h-3.5 w-3.5" />Reconciliation</TabsTrigger>
          <TabsTrigger value="webhook" data-testid="tab-webhooks"><Webhook className="mr-1.5 h-3.5 w-3.5" />Webhooks</TabsTrigger>
          <TabsTrigger value="api"     data-testid="tab-api-explorer"><ScrollText className="mr-1.5 h-3.5 w-3.5" />API Explorer</TabsTrigger>
        </TabsList>

        {/* PERFORMANCE */}
        <TabsContent value="perf">
          <Card title="Endpoint Latency (24h)" icon={LineChart} testid="perf-card"
                 action={<Button size="sm" variant="outline" onClick={() => exportCsv(perf?.endpoints || [], 'etg-performance')}>Export CSV</Button>}>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500 dark:bg-gray-900/50">
                  <tr>
                    <th className="p-2">Endpoint</th><th className="p-2">N</th>
                    <th className="p-2">Avg</th><th className="p-2">P50</th>
                    <th className="p-2">P95</th><th className="p-2">P99</th>
                    <th className="p-2">Success</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                  {perf?.endpoints?.map((r) => (
                    <tr key={r.endpoint} className={r.slow ? 'bg-orange-50 dark:bg-orange-950/20' : ''}>
                      <td className="p-2 font-mono text-xs">{r.endpoint.split('/').slice(-3).join('/')}</td>
                      <td className="p-2">{r.n}</td>
                      <td className="p-2">{fmtMs(r.avg_ms)}</td>
                      <td className="p-2">{fmtMs(r.p50_ms)}</td>
                      <td className="p-2 font-semibold">{fmtMs(r.p95_ms)}</td>
                      <td className="p-2">{fmtMs(r.p99_ms)}</td>
                      <td className="p-2">
                        <Badge variant={r.success_rate >= 0.95 ? 'default' : 'destructive'}>
                          {(r.success_rate * 100).toFixed(1)}%
                        </Badge>
                      </td>
                    </tr>
                  )) || <tr><td colSpan={7} className="p-4 text-center text-gray-500">No performance data yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ERROR ANALYTICS */}
        <TabsContent value="errors">
          <div className="grid gap-4 md:grid-cols-2">
            <Card title="Errors by Category" icon={TrendingUp} testid="err-by-category">
              <ul className="space-y-2 text-sm">
                {Object.entries(errAgg.errors_by_category || {}).map(([k, v]) => (
                  <li key={k} className="flex items-center justify-between">
                    <span className="capitalize text-gray-700 dark:text-gray-200">{k}</span>
                    <Badge>{v}</Badge>
                  </li>
                ))}
                {!Object.keys(errAgg.errors_by_category || {}).length && (
                  <li className="text-gray-500">No errors in window.</li>
                )}
              </ul>
            </Card>
            <Card title="Top Canonical Codes" icon={AlertTriangle} testid="err-top-codes">
              <ul className="space-y-2 text-sm">
                {Object.entries(errAgg.errors_by_canonical_code || {}).slice(0, 10).map(([k, v]) => (
                  <li key={k} className="flex items-center justify-between">
                    <span className="font-mono text-xs">{k}</span>
                    <Badge>{v}</Badge>
                  </li>
                ))}
              </ul>
            </Card>
            <Card title="Errors by Endpoint" icon={Activity} testid="err-by-endpoint">
              <ul className="space-y-2 text-sm">
                {Object.entries(errAgg.errors_by_endpoint || {}).slice(0, 10).map(([k, v]) => (
                  <li key={k} className="flex items-center justify-between">
                    <span className="font-mono text-[11px]">{k?.split('/').slice(-3).join('/') || 'unknown'}</span>
                    <Badge>{v}</Badge>
                  </li>
                ))}
              </ul>
            </Card>
            <Card title="Errors by HTTP Status" icon={Ban} testid="err-by-http">
              <ul className="space-y-2 text-sm">
                {Object.entries(errAgg.errors_by_http_status || {}).map(([k, v]) => (
                  <li key={k} className="flex items-center justify-between">
                    <span className="font-mono text-xs">HTTP {k}</span>
                    <Badge>{v}</Badge>
                  </li>
                ))}
              </ul>
            </Card>
            <Card title="Recent Errors" icon={ScrollText} testid="err-recent"
                   action={<Button size="sm" variant="outline" onClick={() => exportJson(errList, 'etg-errors')}>Export JSON</Button>}>
              <div className="max-h-[400px] overflow-auto">
                <table className="min-w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50 text-left uppercase text-gray-500 dark:bg-gray-900">
                    <tr><th className="p-2">Time</th><th className="p-2">Code</th><th className="p-2">Category</th><th className="p-2">Endpoint</th><th className="p-2">Raw</th></tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                    {errList.map((e, i) => (
                      <tr key={i}>
                        <td className="p-2 whitespace-nowrap">{fmtDate(e.at)}</td>
                        <td className="p-2 font-mono">{e.canonical_code}</td>
                        <td className="p-2">{e.category}</td>
                        <td className="p-2 font-mono text-[10px]">{e.endpoint?.split('/').slice(-3).join('/') || '—'}</td>
                        <td className="p-2 text-gray-500">{e.original_etg_code || e.http_status || '—'}</td>
                      </tr>
                    ))}
                    {!errList.length && <tr><td colSpan={5} className="p-4 text-center text-gray-500">No errors captured.</td></tr>}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* RETRY QUEUE */}
        <TabsContent value="retry">
          <Card title="Retry Queue (non-terminal bookings)" icon={RefreshCw} testid="retry-queue-card"
                 action={<Button size="sm" variant="outline" onClick={() => exportCsv(retryQ, 'etg-retry-queue')}>Export CSV</Button>}>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-gray-900">
                  <tr>
                    <th className="p-2">Booking</th><th className="p-2">State</th>
                    <th className="p-2">Partner Order</th><th className="p-2">Created</th>
                    <th className="p-2">Last Sync</th><th className="p-2">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                  {retryQ.map((b) => (
                    <tr key={b.id} data-testid={`retry-row-${b.id}`}>
                      <td className="p-2 font-mono text-xs">{b.id}</td>
                      <td className="p-2"><Badge>{b.state}</Badge></td>
                      <td className="p-2 font-mono text-xs">{b.partner_order_id}</td>
                      <td className="p-2">{fmtDate(b.created_at)}</td>
                      <td className="p-2">{fmtDate(b.last_sync_at)}</td>
                      <td className="p-2">
                        <Button size="sm" variant="outline" data-testid={`retry-sync-${b.id}`}
                          onClick={async () => {
                            try {
                              await axios.post(
                                `${API}/api/admin/ratehawk/sync/booking/${b.id}`,
                                {}, { headers: authHeaders() },
                              );
                              toast.success(`Sync triggered for ${b.id}`);
                              load();
                            } catch (err) {
                              toast.error(`Sync failed: ${err?.response?.data?.detail || err.message}`);
                            }
                          }}>
                          Force Sync
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!retryQ.length && <tr><td colSpan={6} className="p-4 text-center text-gray-500">Queue empty.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* RECONCILIATION */}
        <TabsContent value="recon">
          <Card title="Reconciliation Issues" icon={ListChecks} testid="recon-card"
                 action={
                   <div className="flex items-center gap-2">
                     <Button size="sm" variant="outline" data-testid="recon-run-btn"
                       onClick={async () => {
                         try {
                           await axios.post(
                             `${API}/api/admin/ratehawk/reconcile/run?sample_limit=100&skip_sync=false`,
                             {}, { headers: authHeaders() },
                           );
                           toast.success('Reconciliation run started');
                           load();
                         } catch (err) {
                           toast.error(`Reconciliation failed: ${err?.response?.data?.detail || err.message}`);
                         }
                       }}>Run Now</Button>
                     <Button size="sm" variant="outline" onClick={() => exportCsv(reconIssues, 'etg-reconciliation')}>Export CSV</Button>
                   </div>
                 }>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-gray-900">
                  <tr>
                    <th className="p-2">Detected</th><th className="p-2">Kind</th>
                    <th className="p-2">Booking</th><th className="p-2">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                  {reconIssues.map((i, idx) => (
                    <tr key={idx}>
                      <td className="p-2 whitespace-nowrap">{fmtDate(i.detected_at)}</td>
                      <td className="p-2"><Badge>{i.kind}</Badge></td>
                      <td className="p-2 font-mono text-xs">
                        {i.booking_id ? (
                          <button className="text-blue-600 hover:underline"
                                  onClick={() => openTimeline(i.booking_id)}>{i.booking_id}</button>
                        ) : '—'}
                      </td>
                      <td className="p-2 text-xs text-gray-600 dark:text-gray-400">
                        {i.diffs ? Object.keys(i.diffs).join(', ') : (i.state || i.book_hash?.slice(0, 12) || '')}
                      </td>
                    </tr>
                  ))}
                  {!reconIssues.length && <tr><td colSpan={4} className="p-4 text-center text-gray-500">No open issues.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* WEBHOOKS */}
        <TabsContent value="webhook">
          <Card title="Webhook Events (last 20)" icon={Webhook} testid="webhook-card"
                 action={<Button size="sm" variant="outline" onClick={() => exportJson(webhookRows, 'etg-webhooks')}>Export JSON</Button>}>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-gray-900">
                  <tr>
                    <th className="p-2">Received</th><th className="p-2">Type</th>
                    <th className="p-2">Status</th><th className="p-2">Partner Order</th>
                    <th className="p-2">Processed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                  {webhookRows.map((w, idx) => (
                    <tr key={idx}>
                      <td className="p-2 whitespace-nowrap">{fmtDate(w.received_at)}</td>
                      <td className="p-2 text-xs">{w.type}</td>
                      <td className="p-2"><Badge>{w.status || '—'}</Badge></td>
                      <td className="p-2 font-mono text-xs">{w.partner_order_id || '—'}</td>
                      <td className="p-2">{w.processed_at ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Clock className="h-4 w-4 text-yellow-500" />}</td>
                    </tr>
                  ))}
                  {!webhookRows.length && <tr><td colSpan={5} className="p-4 text-center text-gray-500">No webhook events yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* API EXPLORER */}
        <TabsContent value="api">
          <Card title="API Explorer — recent ETG calls" icon={Database} testid="api-explorer">
            <p className="mb-3 text-xs text-gray-500">Click a row to inspect request/response payload. Sensitive fields (API key headers) are redacted server-side.</p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-gray-900">
                  <tr>
                    <th className="p-2">Time</th><th className="p-2">Endpoint</th>
                    <th className="p-2">Status</th><th className="p-2">Latency</th>
                    <th className="p-2">OK</th><th className="p-2">Correlation</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                  {errList.slice(0, 20).map((e, idx) => (
                    <tr key={idx} className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/50"
                        onClick={() => setApiLog(e)} data-testid={`api-row-${idx}`}>
                      <td className="p-2 whitespace-nowrap">{fmtDate(e.at)}</td>
                      <td className="p-2 font-mono text-[10px]">{e.endpoint?.split('/').slice(-3).join('/') || '—'}</td>
                      <td className="p-2">{e.http_status || '—'}</td>
                      <td className="p-2">—</td>
                      <td className="p-2"><Ban className="h-3.5 w-3.5 text-red-500" /></td>
                      <td className="p-2 font-mono text-xs">{e.correlation_id?.slice(0, 12) || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Timeline modal */}
      <Dialog open={!!timelineBid} onOpenChange={(o) => !o && (setTimelineBid(null), setTimeline(null))}>
        <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Booking Timeline · <span className="font-mono text-sm">{timelineBid}</span></DialogTitle>
          </DialogHeader>
          {timeline?.error && <div className="text-red-600">{timeline.error}</div>}
          {timeline?.booking && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
                <div><div className="text-xs uppercase text-gray-500">State</div><Badge>{timeline.booking.state}</Badge></div>
                <div><div className="text-xs uppercase text-gray-500">Partner Order</div><div className="font-mono text-xs">{timeline.booking.partner_order_id}</div></div>
                <div><div className="text-xs uppercase text-gray-500">Check-in</div><div>{timeline.booking.checkin}</div></div>
                <div><div className="text-xs uppercase text-gray-500">Check-out</div><div>{timeline.booking.checkout}</div></div>
              </div>
              <ol className="relative border-l border-gray-200 pl-6 dark:border-gray-800">
                {(timeline.booking.events || []).map((e, i) => (
                  <li key={i} className="mb-4">
                    <span className="absolute -left-1.5 flex h-3 w-3 items-center justify-center rounded-full bg-red-600 ring-4 ring-white dark:ring-gray-950" />
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="text-gray-500">{fmtDate(e.at)}</span>
                      {e.to && <Badge variant="outline">→ {e.to}</Badge>}
                      {e.kind && <span className="text-gray-700 dark:text-gray-200">{e.kind}</span>}
                      {e.reason && <span className="text-gray-500">· {e.reason}</span>}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* API log detail modal */}
      <Dialog open={!!apiLog} onOpenChange={(o) => !o && setApiLog(null)}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader><DialogTitle>Error Detail</DialogTitle></DialogHeader>
          {apiLog && (
            <div className="space-y-2 text-sm">
              <div><b>Canonical:</b> <span className="font-mono">{apiLog.canonical_code}</span></div>
              <div><b>Category:</b> {apiLog.category}</div>
              <div><b>Endpoint:</b> <span className="font-mono text-xs">{apiLog.endpoint}</span></div>
              <div><b>HTTP status:</b> {apiLog.http_status ?? '—'}</div>
              <div><b>Raw ETG code:</b> <span className="font-mono">{apiLog.original_etg_code || '—'}</span></div>
              <div><b>Correlation:</b> <span className="font-mono text-xs">{apiLog.correlation_id}</span></div>
              <div><b>Retry action:</b> {apiLog.retry_action}</div>
              <div><b>At:</b> {fmtDate(apiLog.at)}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
